import { useEffect, useRef, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { FileText, FileDown, Pencil, Check, Loader2, Mail, ScrollText, ClipboardList, Megaphone, Newspaper, StickyNote, Wand2 } from "lucide-react";
import { AnswerBody } from "./AnswerBody";
import { CopyButton } from "./CopyButton";
import { V } from "@/lib/vocab";

const TYPE = {
  letter: { label: "Letter", icon: ScrollText },
  email: { label: "Email", icon: Mail },
  notice: { label: "Notice", icon: Megaphone },
  memo: { label: "Memo", icon: StickyNote },
  agenda: { label: "Meeting agenda", icon: ClipboardList },
  minutes: { label: "Meeting minutes", icon: ClipboardList },
  newsletter: { label: "Newsletter", icon: Newspaper },
  announcement: { label: "Announcement", icon: Megaphone },
  other: { label: "Draft", icon: FileText },
};
const REVISIONS = [
  ["Shorter", "Revise the draft: make it noticeably shorter, keep every essential point."],
  ["Firmer", "Revise the draft: firmer and more direct, still respectful."],
  ["Warmer", "Revise the draft: warmer and friendlier in tone."],
  ["More formal", "Revise the draft: more formal, suitable for the record."],
];

const plainForCopy = (d) => [d.subject ? `Subject: ${d.subject}` : "", d.to ? `To: ${d.to}` : "", d.body].filter(Boolean).join("\n\n");

function Field({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs text-ink/60">
      <span className="w-14 shrink-0 font-semibold">{label}</span>
      <input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="flex-1 rounded-lg border border-sand-200 bg-white px-2 py-1 text-xs text-ink focus:outline-none focus:border-ink/40" />
    </label>
  );
}

export function CopilotDraftCard({ tenantId, draft, communityName, onPatch, onRevise, busy }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(draft);
  const [exporting, setExporting] = useState("");
  const taRef = useRef(null);
  useEffect(() => { setLocal(draft); }, [draft]);
  useEffect(() => {
    if (!editing || !taRef.current) return;
    taRef.current.style.height = "auto";
    taRef.current.style.height = `${Math.min(taRef.current.scrollHeight, 520)}px`;
  }, [editing, local.body]);

  const t = TYPE[local.type] || TYPE.other;
  const set = (k) => (v) => setLocal((d) => ({ ...d, [k]: v }));
  const finishEdit = () => { setEditing(false); onPatch?.({ draft: local }); };

  const download = async (format) => {
    setExporting(format);
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/drafts/export`, { format, draft: local }, { responseType: "blob" });
      const name = `${(local.title || t.label).replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "Draft"}.${format}`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success(format === "pdf" ? "PDF downloaded" : "Word document downloaded");
    } catch (e) { toast.error(errMsg(e, "Couldn't build that file")); }
    setExporting("");
  };

  return (
    <div data-testid="copilot-draft-card" className="mt-2 rounded-2xl border border-sand-200 bg-white shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-sand-100 bg-sand-50/70">
        <span className="inline-flex items-center gap-1 rounded-full bg-ink text-white px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-wider">
          <t.icon className="h-3 w-3" strokeWidth={2.5} /> {t.label}
        </span>
        <span className="text-sm font-heading font-bold text-ink truncate flex-1" data-testid="copilot-draft-title">{local.title || t.label}</span>
        {editing ? (
          <button data-testid="copilot-draft-done" onClick={finishEdit} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs font-bold hover:bg-emerald-700 transition-colors">
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> Done
          </button>
        ) : (
          <button data-testid="copilot-draft-edit" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-3 py-1.5 text-xs font-bold text-ink/70 hover:border-ink/40 transition-colors">
            <Pencil className="h-3.5 w-3.5" /> Edit
          </button>
        )}
      </div>

      <div className="px-4 py-3">
        {editing ? (
          <div className="space-y-2">
            <Field label="Title" value={local.title || ""} onChange={set("title")} testid="copilot-draft-title-input" />
            <Field label="To" value={local.to || ""} onChange={set("to")} testid="copilot-draft-to-input" />
            {(local.type === "email" || local.subject) && <Field label="Subject" value={local.subject || ""} onChange={set("subject")} testid="copilot-draft-subject-input" />}
            <textarea ref={taRef} data-testid="copilot-draft-body-input" value={local.body} onChange={(e) => set("body")(e.target.value)}
              className="w-full rounded-xl border border-sand-200 bg-white px-3 py-2.5 text-sm leading-relaxed text-ink font-mono focus:outline-none focus:border-ink/40 resize-none" />
          </div>
        ) : (
          <>
            {(local.to || local.subject) && (
              <div className="mb-3 space-y-0.5 text-xs text-ink/60" data-testid="copilot-draft-meta">
                {local.to && <div><span className="font-semibold">To:</span> {local.to}</div>}
                {local.subject && <div><span className="font-semibold">Subject:</span> {local.subject}</div>}
              </div>
            )}
            <div className="text-sm leading-relaxed text-ink" data-testid="copilot-draft-body">
              <AnswerBody text={local.body} testid="copilot-draft-body-md" />
            </div>
          </>
        )}
      </div>

      <div className="px-4 py-2.5 border-t border-sand-100 bg-sand-50/40 flex items-center gap-1.5 flex-wrap">
        <CopyButton text={plainForCopy(local)} testid="copilot-draft-copy" label="Copy"
          className="border border-sand-200 bg-white !text-ink/70 hover:!text-ink" />
        <button data-testid="copilot-draft-pdf" onClick={() => download("pdf")} disabled={!!exporting}
          className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink/70 hover:text-ink hover:border-ink/40 transition-colors disabled:opacity-50">
          {exporting === "pdf" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />} PDF
        </button>
        <button data-testid="copilot-draft-docx" onClick={() => download("docx")} disabled={!!exporting}
          className="inline-flex items-center gap-1 rounded-full border border-sand-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-ink/70 hover:text-ink hover:border-ink/40 transition-colors disabled:opacity-50">
          {exporting === "docx" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />} Word
        </button>
        <span className="hidden sm:inline text-[10.5px] text-ink/40 ml-1">PDF &amp; Word carry {communityName || `the ${V.lc("org")}`}&rsquo;s letterhead</span>
        {onRevise && (
          <span className="ml-auto inline-flex items-center gap-1 flex-wrap" data-testid="copilot-draft-revisions">
            <Wand2 className="h-3 w-3 text-ink/40" />
            {REVISIONS.map(([l, p]) => (
              <button key={l} data-testid={`copilot-draft-revise-${l.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onRevise(p)} disabled={busy}
                className="rounded-full px-2 py-1 text-[11px] font-semibold text-ink/55 hover:text-ink hover:bg-sand-100 transition-colors disabled:opacity-40">{l}</button>
            ))}
          </span>
        )}
      </div>
    </div>
  );
}
